/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import AppTable from 'components/appTable';
import Empty from 'components/empty';
import RowAction from 'components/rowAction';
import Toolbar, { ToolbarRight } from 'components/toolbar';
import SearchBox from 'components/form/searchBox';
import PanelBoxLayout from 'components/layout/panelBox';
import { useActions } from 'common/hooks/useActions';
import TicketFilterPopup from '../filterForm';
import PaginationApp from 'components/pagination';

import StatusChip from 'components/statusChip';
import Value from 'components/value';
import { formatClock, formatReservationDay } from 'common/helpers/format';

import * as S from './styles';
import * as T from 'common/types';

interface IProps {
  data: { list: []; totalPages: number };
  loading: boolean;
}

export default function ReservationTable({ data, loading }: IProps) {
  const { reservationsRequestAction } = useActions();
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    search: '',
    type: ''
  });

  const fetchData = () => {
    reservationsRequestAction(meta);
  };

  useEffect(() => {
    fetchData();
  }, [meta]);

  const handleChangePage = (page: number) => {
    setMeta({ ...meta, page });
  };

  const handleChangePageSize = (page_size: number) => {
    setMeta({ ...meta, page_size });
  };

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <Toolbar>
          <SearchBox
            onChange={(e) => setMeta({ ...meta, search: e })}
            placeholder="Search reservations"
            value={meta.search}
          />
          <ToolbarRight>
            <TicketFilterPopup setFilter={setMeta} filter={meta} />
          </ToolbarRight>
        </Toolbar>
        <AppTable
          headers={[
            /*
             * Measured on the running panel, not estimated. Subject carries
             * no width and takes what the others leave — 206px at 1280,
             * 366px at 1440.
             */
            { id: 1, key: 'id', title: 'ID', dir: 'left', width: 72 },
            { id: 2, key: 'subject', title: 'Subject', dir: 'left' },
            /* `2025.06.18  16:00–18:00` needs 160 of mono plus 32 of padding.
               The header was "Start, End Date/Time", which promised two dates:
               the API models a reservation as one day with a start and an end
               time, and both the old cell and the old detail screen printed
               the identical date twice. */
            { id: 3, key: 'date', title: 'Date and time', dir: 'left', width: 192 },
            /* `2nd Floor Meeting Room` — 143 of text plus 32. It was 110. */
            { id: 4, key: 'room', title: 'Room', dir: 'left', width: 176 },
            { id: 5, key: 'inviter', title: 'Inviter', dir: 'left', width: 140 },
            { id: 6, key: 'status', title: 'Status', dir: 'left', width: 128 },
            { id: 8, key: 'edit', title: '', type: 'actions', width: 64 }
          ]}
          loading={loading}
          data={data.list}
          empty={
            <Empty
              title={meta.search ? `No reservations match “${meta.search}”` : 'No reservations yet'}
              text={
                meta.search
                  ? 'Try a different term, or clear the filters to see every reservation.'
                  : 'Rooms booked from the employee app appear here.'
              }
            />
          }
          altanfeethiFields={{
            id: (item: T.Pages.ReservationItem) => (
              <S.Div className="text-left">
                <Value mono>{item?.id}</Value>
              </S.Div>
            ),
            subject: (item: T.Pages.ReservationItem) => <S.Div className="text-left">{item.subject}</S.Div>,
            date: (item: T.Pages.ReservationItem) => (
              <S.Div className="text-left">
                <Value mono>
                  {`${formatReservationDay(item.day, item.month, item.year)}  ${formatClock(item.start_time)}–${formatClock(item.end_time)}`}
                </Value>
              </S.Div>
            ),
            room: (item: T.Pages.ReservationItem) => <S.Div className="text-left captal">{item.roomName}</S.Div>,
            inviter: (item: T.Pages.ReservationItem) => <S.Div className="text-left captal">{item.inviter}</S.Div>,
            status: (item: T.Pages.ReservationItem) => (
              <S.Div className="text-left">
                <StatusChip domain="reservation" value={item.status} fallback={<Value />} />
              </S.Div>
            ),
            /*
             * The last table in the product still repeating a bordered button
             * down every row. It is the quiet icon now, like every other
             * "open this record" in the panel.
             */
            edit: (item: T.Pages.ReservationItem) => (
              <RowAction to={`/panel/reservation/${item.id}`} label="Open reservation">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </RowAction>
            )
          }}
        />
      </PanelBoxLayout>

      {data.totalPages ? (
        <PaginationApp
          page={meta.page}
          count={meta.page_size * data.totalPages}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={data.totalPages}
          pageSize={meta.page_size}
          handleChangeTake={handleChangePageSize}
        />
      ) : (
        <></>
      )}
    </S.Content>
  );
}
